import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col, Card, Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import callingleads from "../../assets/images/plot_creation_format.csv";
import { FaFileDownload } from "react-icons/fa";
import { IoMdArrowRoundBack } from "react-icons/io";

const API_URL = process.env.REACT_APP_API_URL;

function CreatePlot() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    project_id: "",
    project_name: "",
    block_id: "",
    block_name: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [file, setFile] = useState(null);
  const [blocks, setBlocks] = useState([]);

  const [selectedProjectCommissionData, setSelectedProjectCommissionData] =
    useState(null);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageModalContent, setMessageModalContent] = useState({
    title: "",
    text: "",
    type: "",
    confirmAction: null,
  });

  const showCustomMessageModal = (title, text, type, confirmAction = null) => {
    setMessageModalContent({ title, text, type, confirmAction });
    setShowMessageModal(true);
  };

  const closeCustomMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({ title: "", text: "", type: "", confirmAction: null });
  };

  const getAuthToken = () => {
    return localStorage.getItem("token");
  };

  useEffect(() => {
    const fetchProjectsList = async () => {
      setLoading(true);
      try {
        const token = getAuthToken();
        if (!token) {
          showCustomMessageModal(
            "Authentication Error",
            "Authentication token not found. Please log in.",
            "error"
          );
          return;
        }

        const response = await fetch(`${API_URL}/project-list-block`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          showCustomMessageModal(
            "Error",
            errorData.message || "Failed to fetch projects.",
            "error"
          );
          return;
        }

        const data = await response.json();
        setProjects(data.data || []);
      } catch (err) {
        console.error("Fetch projects error:", err);
        showCustomMessageModal(
          "Error",
          err.message || "An unexpected error occurred while fetching projects.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProjectsList();
  }, []);

  useEffect(() => {
    const fetchBlocksList = async () => {
      if (formData.project_id) {
        setLoading(true);
        try {
          const token = getAuthToken();
          if (!token) {
            showCustomMessageModal(
              "Authentication Error",
              "Authentication token not found. Please log in.",
              "error"
            );
            return;
          }

          const response = await fetch(`${API_URL}/block-list-plot`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ project_id: formData.project_id }),
          });

          if (!response.ok) {
            const errorData = await response.json();
            showCustomMessageModal(
              "Error",
              errorData.message || "Failed to fetch blocks.",
              "error"
            );
            setBlocks([]);
            return;
          }

          const data = await response.json();
          setBlocks(data.data || []);
        } catch (err) {
          console.error("Fetch blocks error:", err);
          showCustomMessageModal(
            "Error",
            err.message || "An unexpected error occurred while fetching blocks.",
            "error"
          );
          setBlocks([]);
        } finally {
          setLoading(false);
        }
      } else {
        setBlocks([]);
        setFormData((prev) => ({ ...prev, block_id: "", block_name: "" }));
      }
    };

    fetchBlocksList();
  }, [formData.project_id]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prevData) => {
      const newData = { ...prevData, [name]: value };

      if (name === "project_id") {
        const selectedProjectObj = projects.find(
          (proj) => proj.id.toString() === value
        );
        newData.project_name = selectedProjectObj ? selectedProjectObj.name : "";
        newData.block_id = "";
        newData.block_name = "";
        setSelectedProjectCommissionData(selectedProjectObj || null);
      } else if (name === "block_id") {
        const selectedBlockObj = blocks.find(
          (block) => block.id.toString() === value
        );
        newData.block_name = selectedBlockObj ? selectedBlockObj.name : "";
      }

      return newData;
    });

    if (errors[name]) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    const directRequiredFields = [
      "project_id",
      // "block_id",
    ];

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showCustomMessageModal(
        "Validation Error",
        "Please correct the errors in the form.",
        "error"
      );
      return;
    }

    if (!file) {
      Swal.fire({
        icon: "error",
        title: "No CSV File",
        text: "Please select a CSV file before submitting.",
      });
      return;
    }

    setLoading(true);

    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error"
        );
        return;
      }

      const uploadData = new FormData();
      uploadData.append("plotCsv", file);
      uploadData.append("project_id", formData.project_id);
      uploadData.append("project_name", formData.project_name);
      uploadData.append("block_id", formData.block_id);
      uploadData.append("block_name", formData.block_name);
      if (
        formData.property_type === "registry" ||
        formData.property_type === "registrywithpatta"
      ) {
        uploadData.append("resgistry_date", formData.resgistry_date || "");
      }

      const response = await fetch(`${API_URL}/plot-create-csv`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: uploadData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        showCustomMessageModal(
          "Error",
          errorData.message || "Failed to create plot.",
          "error"
        );
        return;
      }

      const result = await response.json();
      if (result.success === "1") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: result.message,
          confirmButtonText: "OK"
        });

        setFormData({
          project_id: "",
          project_name: "",
          block_id: "",
          block_name: "",
        });
        setErrors({});
        setBlocks([]);
        setSelectedProjectCommissionData(null);
        setFile(null);

        setTimeout(() => {
          navigate("/all-plot");
        }, 2000);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: result.message || "Something went wrong!",
          confirmButtonText: "OK"
        });
      }
    } catch (err) {
      console.error("Error creating unit:", err);
      showCustomMessageModal(
        "Error",
        err.message || "An unexpected error occurred.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // return (
  //   <Container className="mt-2">
  //     <div className="row">
  //       <div className="col-md-12">
  //         <div className="card">
  //           <div className="card-header">
  //             <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
  //               <div className="titlepage">
  //                 <h3>By Uploading CSV (Create New Unit/Shop/Villa)</h3>
  //               </div>


  //               <div className="d-flex justify-content-between gap-2">
  //                 <button
  //                   className="btn btn-primary d-flex gap-1"
  //                   onClick={() => navigate(-1)}
  //                 >
  //                   <IoMdArrowRoundBack />
  //                   Back
  //                 </button>
  //                 <OverlayTrigger
  //                   placement="top"
  //                   overlay={<Tooltip>Download Unit Creation CSV Format</Tooltip>}
  //                 >
  //                   <a href={callingleads}
  //                     className="btn btn-primary"
  //                     download="Unit Creation Format.csv"
  //                   >
  //                     Download CSV Format
  //                   </a>
  //                 </OverlayTrigger>
  //               </div>

  //             </div>
  //           </div>
  //           <div className="card-body">
  //             <Form onSubmit={handleSubmit}>
  //               <Row>
  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formProjectId">
  //                     <Form.Label>
  //                       Select Project <span className="text-danger">*</span>
  //                     </Form.Label>
  //                     <Form.Control
  //                       as="select"
  //                       name="project_id"
  //                       value={formData.project_id}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.project_id}
  //                       disabled={loading || projects.length === 0}
  //                     >
  //                       <option value="">Select a Project</option>
  //                       {projects.map((project) => (
  //                         <option key={project.id} value={project.id}>
  //                           {project.name}
  //                         </option>
  //                       ))}
  //                     </Form.Control>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.project_id}
  //                     </Form.Control.Feedback>
  //                     {loading && projects.length === 0 && (
  //                       <small className="text-muted">Loading projects...</small>
  //                     )}
  //                     {!loading && projects.length === 0 && (
  //                       <small className="text-danger">
  //                         No projects available. Please create one first.
  //                       </small>
  //                     )}
  //                   </Form.Group>
  //                 </Col>

  //                 <Col md={6}>
  //                   <Form.Group className="mb-3" controlId="formBlockId">
  //                     <Form.Label>
  //                      Select Block  {/* <span className="text-danger">*</span> */}
  //                     </Form.Label>
  //                     <Form.Control
  //                       as="select"
  //                       name="block_id"
  //                       value={formData.block_id}
  //                       onChange={handleChange}
  //                       isInvalid={!!errors.block_id}
  //                       disabled={!formData.project_id || loading || blocks.length === 0}
  //                     >
  //                       <option value="">Select a Block</option>
  //                       {blocks.map((block) => (
  //                         <option key={block.id} value={block.id}>
  //                           {block.name}
  //                         </option>
  //                       ))}
  //                     </Form.Control>
  //                     <Form.Control.Feedback type="invalid">
  //                       {errors.block_id}
  //                     </Form.Control.Feedback>
  //                     {formData.project_id && loading && blocks.length === 0 && (
  //                       <small className="text-muted">Loading blocks...</small>
  //                     )}
  //                     {formData.project_id && !loading && blocks.length === 0 && (
  //                       <small className="text-danger">
  //                         No blocks available for this project. Please create one
  //                         first.
  //                       </small>
  //                     )}
  //                   </Form.Group>
  //                 </Col>
  //               </Row>
  //               <input
  //                 type="file"
  //                 accept=".csv"
  //                 onChange={(e) => {
  //                   const selectedFile = e.target.files[0];
  //                   if (!selectedFile) return;

  //                   const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
  //                   if (fileExtension !== "csv") {
  //                     Swal.fire({
  //                       icon: "error",
  //                       title: "Invalid File",
  //                       text: "Only CSV files are allowed!",
  //                     });
  //                     e.target.value = null;
  //                     setFile(null);
  //                     return;
  //                   }
  //                   setFile(selectedFile);
  //                 }}
  //               />
  //               <div className="submitbutton">
  //                 <Button
  //                   type="submit"
  //                   className="submitbutton_design mt-2"
  //                   disabled={loading}
  //                 >
  //                   {loading ? "Creating..." : "Create Unit"}
  //                 </Button>
  //               </div>
  //             </Form>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   </Container>
  // );
return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(30, 87, 153, 0.3)",
                flexShrink: 0,
              }}
            >
              📤
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Upload CSV — Create New Plot
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                Bulk create plots, shops & villas via CSV file
              </p>
            </div>

            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={() => navigate(-1)}
                style={{
                  padding: "12px 24px",
                  borderRadius: "12px",
                  border: "none",
                  background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  boxShadow: "0 8px 24px rgba(30, 87, 153, 0.3)",
                }}
              >
                <IoMdArrowRoundBack /> Back
              </button>

              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>Download Unit Creation CSV Format</Tooltip>
                }
              >
                <a
                  href={callingleads}
                  download="Unit Creation Format.csv"
                  style={{
                    padding: "12px 24px",
                    borderRadius: "12px",
                    border: "none",
                    background: "linear-gradient(135deg, #1e5799, #2b6cb0)",
                    color: "#ffffff",
                    fontSize: "14px",
                    fontWeight: "700",
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    boxShadow: "0 8px 24px rgba(30, 87, 153, 0.3)",
                  }}
                >
                  <FaFileDownload /> Download Format
                </a>
              </OverlayTrigger>
            </div>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background: "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "30px" }}>
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formProjectId">
                      <Form.Label>
                        Select Project <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        name="project_id"
                        value={formData.project_id}
                        onChange={handleChange}
                        isInvalid={!!errors.project_id}
                        disabled={loading || projects.length === 0}
                      >
                        <option value="">Select a Project</option>
                        {projects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.project_id}
                      </Form.Control.Feedback>
                      {loading && projects.length === 0 && (
                        <small className="text-muted">
                          Loading projects...
                        </small>
                      )}
                      {!loading && projects.length === 0 && (
                        <small className="text-danger">
                          No projects available. Please create one first.
                        </small>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="formBlockId">
                      <Form.Label>Select Block</Form.Label>
                      <Form.Control
                        as="select"
                        name="block_id"
                        value={formData.block_id}
                        onChange={handleChange}
                        isInvalid={!!errors.block_id}
                        disabled={
                          !formData.project_id ||
                          loading ||
                          blocks.length === 0
                        }
                      >
                        <option value="">Select a Block</option>
                        {blocks.map((block) => (
                          <option key={block.id} value={block.id}>
                            {block.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.block_id}
                      </Form.Control.Feedback>
                      {formData.project_id &&
                        loading &&
                        blocks.length === 0 && (
                          <small className="text-muted">
                            Loading blocks...
                          </small>
                        )}
                      {formData.project_id &&
                        !loading &&
                        blocks.length === 0 && (
                          <small className="text-danger">
                            No blocks available for this project. Please create
                            one first.
                          </small>
                        )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* 🎯 CSV UPLOAD AREA */}
                <div
                  style={{
                    border: "2px dashed #cbd5e1",
                    borderRadius: "14px",
                    padding: "30px",
                    textAlign: "center",
                    background: "#f8fafc",
                    marginTop: "10px",
                    marginBottom: "20px",
                  }}
                >
                  <div style={{ fontSize: "40px", marginBottom: "10px" }}>
                    📄
                  </div>
                  <p
                    style={{
                      margin: "0 0 12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1e3a5f",
                    }}
                  >
                    {file ? `Selected: ${file.name}` : "Choose a CSV file to upload"}
                  </p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      if (!selectedFile) return;

                      const fileExtension = selectedFile.name
                        .split(".")
                        .pop()
                        .toLowerCase();
                      if (fileExtension !== "csv") {
                        Swal.fire({
                          icon: "error",
                          title: "Invalid File",
                          text: "Only CSV files are allowed!",
                        });
                        e.target.value = null;
                        setFile(null);
                        return;
                      }
                      setFile(selectedFile);
                    }}
                    style={{
                      padding: "10px",
                      borderRadius: "10px",
                      border: "1.5px solid #e2e8f0",
                      background: "#ffffff",
                      cursor: "pointer",
                      width: "100%",
                      maxWidth: "400px",
                    }}
                  />
                </div>

                {/* Submit Button */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    paddingTop: "20px",
                    borderTop: "1.5px dashed #e2e8f0",
                  }}
                >
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      padding: "13px 42px",
                      borderRadius: "12px",
                      border: "none",
                      background: loading
                        ? "#cbd5e1"
                        : "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      color: "#ffffff",
                      fontSize: "14.5px",
                      fontWeight: "800",
                      letterSpacing: "0.5px",
                      cursor: loading ? "not-allowed" : "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 24px rgba(30, 87, 153, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                      minWidth: "220px",
                    }}
                  >
                    {loading ? (
                      <>
                        <span
                          style={{
                            width: "16px",
                            height: "16px",
                            border: "2px solid rgba(255,255,255,0.3)",
                            borderTop: "2px solid #ffffff",
                            borderRadius: "50%",
                            display: "inline-block",
                            animation: "spin 0.8s linear infinite",
                          }}
                        />
                        Creating...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>

                {/* Footer Note */}
                <div
                  style={{
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "1.5px dashed #e2e8f0",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontSize: "12.5px",
                      color: "#94a3b8",
                      fontWeight: "500",
                    }}
                  >
                    📌 Download the CSV format above and fill it correctly
                    before uploading
                  </p>
                </div>
              </Form>
            </div>
          </div>

          {/* Footer Copyright */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      {/* 🎯 MESSAGE MODAL */}
      {showMessageModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className={`modal-content ${
                messageModalContent.type === "success"
                  ? "border-success"
                  : messageModalContent.type === "error"
                    ? "border-danger"
                    : "border-warning"
              }`}
            >
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5
                  className={`modal-title ${
                    messageModalContent.type === "success"
                      ? "text-success"
                      : messageModalContent.type === "error"
                        ? "text-danger"
                        : "text-warning"
                  }`}
                >
                  {messageModalContent.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeCustomMessageModal}
                ></button>
              </div>
              <div className="modal-body text-secondary">
                <p style={{ whiteSpace: "pre-line" }}>
                  {messageModalContent.text}
                </p>
              </div>
              <div className="modal-footer justify-content-center">
                {messageModalContent.confirmAction ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={closeCustomMessageModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={
                        messageModalContent.type === "warning"
                          ? "warning"
                          : "primary"
                      }
                      onClick={() => {
                        messageModalContent.confirmAction();
                        closeCustomMessageModal();
                      }}
                    >
                      Confirm
                    </Button>
                  </>
                ) : (
                  <Button
                    variant={
                      messageModalContent.type === "success"
                        ? "success"
                        : messageModalContent.type === "error"
                          ? "danger"
                          : "primary"
                    }
                    onClick={closeCustomMessageModal}
                  >
                    OK
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}

export default CreatePlot;